import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import loreCornerDetail from '../../assets/lore/lore_details.png';
import loreButton from '../../assets/lore/lore_button.png';

import keyIcon from '../../assets/key_icon.png';
import teethIcon from '../../assets/teeth_icon.png';
import potionIcon from '../../assets/potion_icon.png';
import torchIcon from '../../assets/torch_icon.png';

const RESOURCE_META = {
  keys: { label: 'Keys', icon: keyIcon },
  potions: { label: 'Potions', icon: potionIcon },
  teeth: { label: 'Teeth', icon: teethIcon },
  torches: { label: 'Torches', icon: torchIcon },
};

function CornerDetail({ className = '', rotationClass = '' }) {
  return (
    <img
      src={loreCornerDetail}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={`pointer-events-none absolute h-[52px] w-[52px] select-none opacity-80 ${rotationClass} ${className}`}
    />
  );
}

function FooterButton({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative h-[68px] w-[250px] appearance-none border-0 bg-transparent p-0 text-white shadow-none outline-none transition hover:opacity-90 active:scale-[0.99]"
      style={{ fontFamily: 'Karma, serif' }}
    >
      <img
        src={loreButton}
        alt=""
        aria-hidden="true"
        draggable={false}
        className="pointer-events-none absolute inset-0 h-full w-full select-none object-contain"
      />
      <span className="relative z-[1] flex h-full w-full items-center justify-center text-[18px] font-semibold translate-y-[2px]">
        {label}
      </span>
    </button>
  );
}

export function SimpleChestResourceModal({ open, rewards = [], onLootAll }) {
  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[12000] flex items-center justify-center overflow-hidden p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <motion.div
            className="relative z-[1] w-[520px] max-w-[94vw] bg-black/80 px-6 pb-8 pt-5 text-white shadow-[0_20px_60px_rgba(0,0,0,0.72)] sm:px-8 sm:pb-10 sm:pt-6"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ fontFamily: 'Karma, serif' }}
          >
            <CornerDetail className="left-4 top-4" rotationClass="rotate-180" />
            <CornerDetail className="right-4 top-4" rotationClass="-rotate-90" />
            <CornerDetail className="left-4 bottom-4" rotationClass="rotate-90" />
            <CornerDetail className="right-4 bottom-4" />

            <div className="relative z-[1] mx-auto flex max-h-[82svh] w-full max-w-[420px] flex-col items-center pt-6 sm:pt-8">
              <h2 className="text-center text-[18px] font-semibold text-white sm:text-[20px]">
                Treasure Chest
              </h2>

              <div
                className="mt-5 min-h-0 w-full flex-1 overflow-y-auto pr-1 overscroll-contain"
                style={{ touchAction: 'pan-y' }}
                onWheelCapture={(e) => e.stopPropagation()}
                onTouchStartCapture={(e) => e.stopPropagation()}
                onTouchMoveCapture={(e) => e.stopPropagation()}
              >
                <div className="space-y-3">
                  {rewards.map((reward, index) => {
                    const meta = RESOURCE_META[reward?.kind] || RESOURCE_META.teeth;
                    const amount = Math.max(0, Math.floor(Number(reward?.amount) || 0));

                    return (
                      <div
                        key={`${reward?.kind || 'resource'}-${index}`}
                        className="flex w-full items-center gap-3 rounded-[18px] border border-white/10 bg-black/90 px-4 py-3"
                      >
                        <img
                          src={meta.icon}
                          alt={meta.label}
                          className="h-12 w-12 shrink-0 object-contain"
                          draggable={false}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-[18px] font-semibold leading-tight break-words text-white">
                            {meta.label}
                          </div>
                        </div>
                        <div className="text-[24px] leading-none text-white/90">x{amount}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-6 flex w-full justify-center">
                <FooterButton label="Loot all" onClick={onLootAll} />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
